import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type QuestionType = 'single_select' | 'multi_select' | 'free_text' | 'boolean';

interface QuestionDefinition {
  id: string;
  label: string;
  type: QuestionType;
  required: boolean;
  options?: string[];
  placeholder?: string;
}

interface Questionnaire {
  id: number;
  name: string;
  version: number;
  schema_: { questions: QuestionDefinition[] };
  group_ids: number[];
}

interface QuestionnaireListItem {
  id: number;
  name: string;
  version: number;
  question_count: number;
  group_ids: number[];
}

interface PersonaGroupMinimal {
  group_id: number;
  name: string;
}

// ---------------------------------------------------------------------------
// Question Editor Row
// ---------------------------------------------------------------------------

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'single_select', label: 'Single Select' },
  { value: 'multi_select', label: 'Multi Select' },
  { value: 'free_text', label: 'Free Text' },
  { value: 'boolean', label: 'Yes / No' },
];

const QuestionRow: React.FC<{
  question: QuestionDefinition;
  index: number;
  onChange: (updated: QuestionDefinition) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}> = ({ question, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [optionInput, setOptionInput] = useState('');
  const needsOptions = question.type === 'single_select' || question.type === 'multi_select';

  const addOption = () => {
    const val = optionInput.trim();
    if (val && !(question.options || []).includes(val)) {
      onChange({ ...question, options: [...(question.options || []), val] });
    }
    setOptionInput('');
  };

  return (
    <div style={{
      padding: '16px',
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '10px',
      border: '1px solid rgba(255,255,255,0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
          QUESTION {index + 1}
        </span>
        <div style={{ display: 'flex', gap: '4px' }}>
          {!isFirst && (
            <button type="button" onClick={onMoveUp} style={iconBtnStyle} title="Move up">▲</button>
          )}
          {!isLast && (
            <button type="button" onClick={onMoveDown} style={iconBtnStyle} title="Move down">▼</button>
          )}
          <button type="button" onClick={onRemove} style={{ ...iconBtnStyle, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)' }} title="Remove">✕</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
        <div>
          <label style={fieldLabelStyle}>Question ID</label>
          <input
            type="text"
            value={question.id}
            onChange={(e) => onChange({ ...question, id: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
            placeholder="e.g., gender"
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label style={fieldLabelStyle}>Label</label>
          <input
            type="text"
            value={question.label}
            onChange={(e) => onChange({ ...question, label: e.target.value })}
            placeholder="e.g., Gender"
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label style={fieldLabelStyle}>Type</label>
          <select
            value={question.type}
            onChange={(e) => {
              const newType = e.target.value as QuestionType;
              const updated: QuestionDefinition = { ...question, type: newType };
              if (newType === 'free_text' || newType === 'boolean') {
                delete updated.options;
              } else if (!updated.options) {
                updated.options = [];
              }
              onChange(updated);
            }}
            style={{ ...fieldInputStyle, minWidth: '140px' }}
          >
            {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={question.required}
            onChange={(e) => onChange({ ...question, required: e.target.checked })}
            style={{ accentColor: '#6366f1' }}
          />
          Required
        </label>
        {question.type === 'free_text' && (
          <div style={{ flex: 1 }}>
            <input
              type="text"
              value={question.placeholder || ''}
              onChange={(e) => onChange({ ...question, placeholder: e.target.value })}
              placeholder="Placeholder text..."
              style={{ ...fieldInputStyle, width: '100%' }}
            />
          </div>
        )}
      </div>

      {needsOptions && (
        <div>
          <label style={fieldLabelStyle}>Options</label>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
            <input
              type="text"
              value={optionInput}
              onChange={(e) => setOptionInput(e.target.value)}
              placeholder="Add option..."
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
              style={{ ...fieldInputStyle, flex: 1 }}
            />
            <button type="button" onClick={addOption} style={{
              padding: '7px 14px',
              borderRadius: '6px',
              border: '1px solid rgba(99,102,241,0.4)',
              background: 'rgba(99,102,241,0.15)',
              color: '#a5b4fc',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
            }}>+</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '24px' }}>
            {(question.options || []).map((opt, i) => (
              <span key={i} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 10px',
                borderRadius: '14px',
                background: 'rgba(99,102,241,0.15)',
                border: '1px solid rgba(99,102,241,0.3)',
                color: '#c7d2fe',
                fontSize: '12px',
              }}>
                {opt}
                <button type="button" onClick={() => onChange({ ...question, options: (question.options || []).filter((_, j) => j !== i) })} style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '0 2px', fontSize: '14px', lineHeight: 1,
                }}>×</button>
              </span>
            ))}
            {(question.options || []).length === 0 && (
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>No options added yet</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Questionnaire Modal
// ---------------------------------------------------------------------------

interface QuestionnaireModalProps {
  questionnaire?: Questionnaire | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, questions: QuestionDefinition[], groupIds: number[]) => void;
  isLoading: boolean;
  allGroups: PersonaGroupMinimal[];
  assignedGroupMap: Record<number, number>; // group_id -> questionnaire_id
}

const QuestionnaireModal: React.FC<QuestionnaireModalProps> = ({
  questionnaire, isOpen, onClose, onSave, isLoading, allGroups, assignedGroupMap,
}) => {
  const [name, setName] = useState('');
  const [questions, setQuestions] = useState<QuestionDefinition[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const groupSearchRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (questionnaire) {
      setName(questionnaire.name);
      setQuestions(questionnaire.schema_?.questions || []);
      setSelectedGroupIds(questionnaire.group_ids || []);
    } else {
      setName('');
      setQuestions([]);
      setSelectedGroupIds([]);
    }
  }, [questionnaire]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (groupSearchRef.current && !groupSearchRef.current.contains(e.target as Node)) {
        setGroupDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addQuestion = () => {
    setQuestions(prev => [...prev, {
      id: '',
      label: '',
      type: 'single_select',
      required: true,
      options: [],
    }]);
  };

  const updateQuestion = (index: number, updated: QuestionDefinition) => {
    setQuestions(prev => prev.map((q, i) => i === index ? updated : q));
  };

  const removeQuestion = (index: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const moveQuestion = (from: number, to: number) => {
    setQuestions(prev => {
      const arr = [...prev];
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return arr;
    });
  };

  const toggleGroup = (groupId: number) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const isValid = name.trim() !== '' && questions.length > 0 && questions.every(q => q.id && q.label);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(name.trim(), questions, selectedGroupIds);
  };

  // Groups available: either unassigned or already assigned to THIS questionnaire
  const availableGroups = allGroups.filter(g => {
    const assignedTo = assignedGroupMap[g.group_id];
    return assignedTo === undefined || assignedTo === questionnaire?.id;
  });

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h2>{questionnaire ? 'Edit Questionnaire' : 'Create Questionnaire'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px' }}>
          <div>
            <label style={fieldLabelStyle}>Questionnaire Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Default Demographics"
              style={{ ...fieldInputStyle, width: '100%' }}
              required
            />
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <label style={{ ...fieldLabelStyle, marginBottom: 0 }}>Questions ({questions.length})</label>
              <button type="button" onClick={addQuestion} style={{
                padding: '6px 16px',
                borderRadius: '6px',
                border: '1px solid rgba(99,102,241,0.4)',
                background: 'rgba(99,102,241,0.15)',
                color: '#a5b4fc',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
              }}>+ Add Question</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
              {questions.map((q, i) => (
                <QuestionRow
                  key={i}
                  question={q}
                  index={i}
                  onChange={(updated) => updateQuestion(i, updated)}
                  onRemove={() => removeQuestion(i)}
                  onMoveUp={() => moveQuestion(i, i - 1)}
                  onMoveDown={() => moveQuestion(i, i + 1)}
                  isFirst={i === 0}
                  isLast={i === questions.length - 1}
                />
              ))}
              {questions.length === 0 && (
                <div style={{
                  padding: '32px',
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.35)',
                  fontStyle: 'italic',
                  border: '2px dashed rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                }}>
                  No questions yet. Click "+ Add Question" to get started.
                </div>
              )}
            </div>
          </div>

          <div>
            <label style={fieldLabelStyle}>Assign to Groups</label>
            {selectedGroupIds.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginBottom: '8px',
              }}>
                {selectedGroupIds.map(gid => {
                  const group = allGroups.find(g => g.group_id === gid);
                  return (
                    <span key={gid} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      background: 'rgba(99,102,241,0.15)',
                      border: '1px solid rgba(99,102,241,0.3)',
                      fontSize: '13px',
                      color: '#c7d2fe',
                    }}>
                      {group?.name || `Group ${gid}`}
                      <button
                        type="button"
                        onClick={() => toggleGroup(gid)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255,255,255,0.5)',
                          cursor: 'pointer',
                          padding: '0 2px',
                          fontSize: '15px',
                          lineHeight: 1,
                        }}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
            <div ref={groupSearchRef} style={{ position: 'relative' }}>
              <input
                type="text"
                value={groupSearch}
                onChange={e => { setGroupSearch(e.target.value); setGroupDropdownOpen(true); }}
                onFocus={() => setGroupDropdownOpen(true)}
                placeholder="Search groups..."
                style={{ ...fieldInputStyle, width: '100%' }}
              />
              {groupDropdownOpen && (() => {
                const filtered = availableGroups
                  .filter(g => !selectedGroupIds.includes(g.group_id))
                  .filter(g => g.name.toLowerCase().includes(groupSearch.toLowerCase()));
                return filtered.length > 0 ? (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: '#1e1e2e',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '8px',
                    marginTop: '4px',
                    zIndex: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}>
                    {filtered.map(g => (
                      <div
                        key={g.group_id}
                        onClick={() => { toggleGroup(g.group_id); setGroupSearch(''); }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color: 'rgba(255,255,255,0.7)',
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(99,102,241,0.15)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {g.name}
                      </div>
                    ))}
                  </div>
                ) : groupSearch ? (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#1e1e2e',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '8px',
                    marginTop: '4px',
                    zIndex: 10,
                    padding: '12px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.35)',
                    fontStyle: 'italic',
                  }}>
                    No matching groups
                  </div>
                ) : null;
              })()}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading || !isValid}>
              {isLoading ? 'Saving...' : (questionnaire ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const OnboardingManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<Questionnaire | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: questionnaires = [], isLoading, error, refetch } = useQuery<QuestionnaireListItem[]>({
    queryKey: ['questionnaires'],
    queryFn: async () => {
      const res = await fetch('/api/onboarding/questionnaires');
      if (!res.ok) throw new Error('Failed to fetch questionnaires');
      return res.json();
    },
    refetchInterval: 30000,
  });

  const { data: groups = [] } = useQuery<PersonaGroupMinimal[]>({
    queryKey: ['persona-groups-minimal'],
    queryFn: async () => {
      const res = await fetch('/api/persona-groups');
      if (!res.ok) throw new Error('Failed to fetch groups');
      return res.json();
    },
  });

  // Map group_id -> questionnaire_id for all assigned groups
  const assignedGroupMap: Record<number, number> = {};
  for (const q of questionnaires) {
    for (const gid of q.group_ids) {
      assignedGroupMap[gid] = q.id;
    }
  }

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; schema_: { questions: QuestionDefinition[] }; group_ids: number[] }) => {
      const res = await fetch('/api/onboarding/questionnaires', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: data.name, schema_: data.schema_ }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create questionnaire');
      }
      const created = await res.json();
      // Assign groups if any selected
      if (data.group_ids.length > 0) {
        const assignRes = await fetch(`/api/onboarding/questionnaires/${created.id}/groups`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ group_ids: data.group_ids }),
        });
        if (!assignRes.ok) {
          const err = await assignRes.json();
          throw new Error(err.detail || 'Created but failed to assign groups');
        }
      }
      return created;
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedQuestionnaire(null);
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: number; name: string; schema_: { questions: QuestionDefinition[] }; group_ids: number[] }) => {
      const res = await fetch(`/api/onboarding/questionnaires/${data.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: data.name, schema_: data.schema_ }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update questionnaire');
      }
      const assignRes = await fetch(`/api/onboarding/questionnaires/${data.id}/groups`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_ids: data.group_ids }),
      });
      if (!assignRes.ok) {
        const err = await assignRes.json();
        throw new Error(err.detail || 'Updated but failed to assign groups');
      }
      return res.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedQuestionnaire(null);
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/onboarding/questionnaires/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete questionnaire');
      }
      return res.json();
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const handleCreate = useCallback(() => {
    setSelectedQuestionnaire(null);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleEdit = useCallback(async (item: QuestionnaireListItem) => {
    setActionError(null);
    try {
      const res = await fetch(`/api/onboarding/questionnaires/${item.id}`);
      if (!res.ok) throw new Error('Failed to load questionnaire');
      const full: Questionnaire = await res.json();
      setSelectedQuestionnaire(full);
      setIsModalOpen(true);
    } catch (err: any) {
      setActionError(err.message);
    }
  }, []);

  const handleDelete = useCallback((item: QuestionnaireListItem) => {
    if (window.confirm(`Delete questionnaire "${item.name}"? This will unassign all groups.`)) {
      deleteMutation.mutate(item.id);
    }
  }, [deleteMutation]);

  const handleSave = useCallback((name: string, questions: QuestionDefinition[], groupIds: number[]) => {
    const payload = { name, schema_: { questions }, group_ids: groupIds };
    if (selectedQuestionnaire) {
      updateMutation.mutate({ id: selectedQuestionnaire.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }, [selectedQuestionnaire, createMutation, updateMutation]);

  const groupNameMap: Record<number, string> = {};
  for (const g of groups) {
    groupNameMap[g.group_id] = g.name;
  }

  if (isLoading) return <div className="loading">Loading questionnaires...</div>;
  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <div className="region-manager">
      <div className="page-header">
        <h1>Onboarding Questionnaires</h1>
        <div className="page-actions">
          <button onClick={() => refetch()} className="btn-secondary">Refresh</button>
          <button onClick={handleCreate} className="btn-primary">+ New Questionnaire</button>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {questionnaires.map(q => (
          <div key={q.id} className="group-card">
            <div className="group-header">
              <div className="group-title">
                <h3>{q.name}</h3>
                <span className="group-count">v{q.version} · {q.question_count} question{q.question_count !== 1 ? 's' : ''}</span>
              </div>
              <div className="group-actions">
                <button onClick={() => handleEdit(q)} className="btn-icon" title="Edit">✏️</button>
                <button onClick={() => handleDelete(q)} className="btn-icon btn-danger" title="Delete">🗑️</button>
              </div>
            </div>
            <div className="group-details">
              <div>
                <strong>Assigned Groups:</strong>{' '}
                {q.group_ids.length > 0
                  ? q.group_ids.map(gid => groupNameMap[gid] || `Group ${gid}`).join(', ')
                  : <span style={{ color: 'rgba(255,255,255,0.4)' }}>None</span>
                }
              </div>
            </div>
          </div>
        ))}

        {questionnaires.length === 0 && (
          <div className="empty-state">
            <h3>No questionnaires yet</h3>
            <p>Create your first onboarding questionnaire to define what participants are asked during sign-up.</p>
            <button onClick={handleCreate} className="btn-primary">+ Create First Questionnaire</button>
          </div>
        )}
      </div>

      <QuestionnaireModal
        questionnaire={selectedQuestionnaire}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedQuestionnaire(null); setActionError(null); }}
        onSave={handleSave}
        isLoading={createMutation.isPending || updateMutation.isPending}
        allGroups={groups}
        assignedGroupMap={assignedGroupMap}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.7)',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const fieldInputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.05)',
  color: 'white',
  fontSize: '13px',
  boxSizing: 'border-box',
};

const iconBtnStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.5)',
  cursor: 'pointer',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '12px',
};

export default OnboardingManager;
